import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  Paper,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import { useFormulaStore } from '../store/formulaStore';
import { formulaService } from '../services/formulaService';
import type { FormulaValidationResult } from '../types/formula';

interface FormulaEditorProps {
  value: string;
  onChange: (formula: string) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  minRows?: number;
}

const OPERADORES = [
  { label: '+', value: ' + ', desc: 'Suma' },
  { label: '-', value: ' - ', desc: 'Resta' },
  { label: '*', value: ' * ', desc: 'Multiplicación' },
  { label: '/', value: ' / ', desc: 'División' },
  { label: '(', value: '(', desc: 'Paréntesis abrir' },
  { label: ')', value: ')', desc: 'Paréntesis cerrar' },
  { label: '>', value: ' > ', desc: 'Mayor que' },
  { label: '<', value: ' < ', desc: 'Menor que' },
  { label: '>=', value: ' >= ', desc: 'Mayor o igual' },
  { label: '<=', value: ' <= ', desc: 'Menor o igual' },
  { label: '==', value: ' == ', desc: 'Igual' },
  { label: '!=', value: ' != ', desc: 'Diferente' },
  { label: '&&', value: ' && ', desc: 'Y lógico' },
  { label: '||', value: ' || ', desc: 'O lógico' },
];

const FormulaEditor: React.FC<FormulaEditorProps> = ({
  value,
  onChange,
  label = 'Fórmula',
  helperText,
  error,
  disabled = false,
  minRows = 3,
}) => {
  const { grupos, funciones, fetchGrupos, fetchFunciones } = useFormulaStore();
  const [validation, setValidation] = useState<FormulaValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (grupos.length === 0) fetchGrupos();
    if (funciones.length === 0) fetchFunciones();
  }, [grupos.length, funciones.length, fetchGrupos, fetchFunciones]);

  const insertAtCursor = (text: string) => {
    if (disabled) return;
    const input = inputRef.current;
    if (input) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = value.substring(0, start) + text + value.substring(end);
      onChange(newValue);
      setTimeout(() => {
        input.focus();
        const newPos = start + text.length;
        input.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      onChange(value + text);
    }
    setValidation(null);
  };

  const handleValidate = async () => {
    if (!value.trim()) return;
    setValidating(true);
    try {
      const result = await formulaService.validarFormula(value);
      setValidation(result);
    } catch {
      setValidation({
        isValid: false,
        error: 'Error al validar la fórmula',
        testResult: null,
        usedVariables: [],
        missingVariables: [],
      });
    }
    setValidating(false);
  };

  const functionsByCategory = funciones.reduce((acc, fn) => {
    if (!acc[fn.category]) acc[fn.category] = [];
    acc[fn.category].push(fn);
    return acc;
  }, {} as Record<string, typeof funciones>);

  return (
    <Box>
      {/* Campo de fórmula */}
      <TextField
        label={label}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setValidation(null);
        }}
        inputRef={inputRef}
        error={error || (validation !== null && !validation.isValid)}
        helperText={helperText}
        fullWidth
        multiline
        minRows={minRows}
        disabled={disabled}
        placeholder="Ej: [BASICO] / 30 * [DIAS_TRABAJADOS]"
        sx={{
          '& .MuiInputBase-input': {
            fontFamily: 'Consolas, Monaco, monospace',
            fontSize: '0.9rem',
          },
        }}
        slotProps={{
          input: {
            endAdornment: value.trim() ? (
              <IconButton
                onClick={handleValidate}
                disabled={validating}
                size="small"
                color={validation?.isValid ? 'success' : 'primary'}
                sx={{ alignSelf: 'flex-start', mt: 0.5 }}
              >
                <TestIcon />
              </IconButton>
            ) : null,
          },
        }}
      />

      {/* Resultado de validación */}
      {validation && (
        <Alert
          severity={validation.isValid ? 'success' : 'error'}
          icon={validation.isValid ? <ValidIcon /> : <ErrorIcon />}
          sx={{ mt: 1, mb: 1 }}
        >
          {validation.isValid ? (
            <>
              Fórmula válida. Resultado de prueba: <strong>{String(validation.testResult)}</strong>
              {validation.usedVariables.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  Variables: {validation.usedVariables.map(v => (
                    <Chip key={v} label={v} size="small" sx={{ mr: 0.5, mt: 0.5 }} />
                  ))}
                </Box>
              )}
            </>
          ) : (
            <>
              {validation.error}
              {validation.missingVariables.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  Variables no encontradas: {validation.missingVariables.map(v => (
                    <Chip key={v} label={v} size="small" color="error" sx={{ mr: 0.5, mt: 0.5 }} />
                  ))}
                </Box>
              )}
            </>
          )}
        </Alert>
      )}

      {/* Paleta de herramientas */}
      {!disabled && (
        <Paper variant="outlined" sx={{ mt: 1, maxHeight: 350, overflow: 'auto' }}>
          {/* Operadores */}
          <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {OPERADORES.map((op) => (
              <Tooltip key={op.label} title={op.desc}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => insertAtCursor(op.value)}
                  sx={{ minWidth: 36, px: 1, fontFamily: 'monospace', fontWeight: 'bold' }}
                >
                  {op.label}
                </Button>
              </Tooltip>
            ))}
          </Box>

          <Divider />

          {/* Variables por grupo */}
          {grupos.map((grupo) => (
            <Accordion key={grupo.idGrupo} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {grupo.nombre}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {grupo.parametros
                  .filter(p => p.activo)
                  .sort((a, b) => a.orden - b.orden)
                  .map((param) => (
                    <Tooltip key={param.idParametro} title={param.descripcion}>
                      <Chip
                        label={param.clave}
                        size="small"
                        color="info"
                        variant="outlined"
                        onClick={() => insertAtCursor(`[${param.clave}]`)}
                        clickable
                      />
                    </Tooltip>
                  ))}
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Funciones por categoría */}
          {Object.entries(functionsByCategory).map(([category, fns]) => (
            <Accordion key={category} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight="bold" color="secondary">
                  Fn: {category}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {fns.map((fn) => (
                  <Tooltip key={fn.name} title={`${fn.syntax} - ${fn.description}`}>
                    <Chip
                      label={fn.name}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      onClick={() => insertAtCursor(`${fn.name}(`)}
                      clickable
                    />
                  </Tooltip>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default FormulaEditor;
